import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';

/**
 * The document lifecycle, mirroring INVOICE_REQUEST_STATUSES in
 * api-invoice-requests/models.js.
 *
 *   draft → requested → under_review → approved
 *         → proforma_requested (Proforma created; no Proforma approval step)
 *         → invoice_pending_approval → sent
 *
 * Quotations and tax invoices retain their approval workflows. Proforma is not
 * an approvable stage; its stage type remains for rejection handling only.
 */
export type InvoiceRequestStatus =
  | 'draft'
  | 'requested'
  | 'under_review'
  | 'approved'
  | 'proforma_requested'
  | 'proforma_under_review'
  | 'proforma_approved'
  | 'proforma_rejected'
  | 'invoice_pending_approval'
  | 'revision_needed'
  | 'rejected'
  | 'sent'
  | 'confirmed';

/** The two signatures a stage requires, in the order they must be given. */
export type ApproverRole = 'accounts' | 'superadmin';

/** Who a stage is waiting on. 'none' once finished, rejected, or between stages. */
export type WaitingOn = ApproverRole | 'none';

export type ApprovalStage = 'quotation' | 'proforma' | 'invoice';

/** Per-role decision state for one stage. */
export interface StageApprovals {
  accounts: 'pending' | 'approved' | 'rejected';
  superadmin: 'pending' | 'approved' | 'rejected';
}

export interface InvoiceRequestLine {
  catalogItemId?: string | null;
  code?: string;
  name: string;
  description?: string;
  unit?: string;
  hsnSac?: string;
  quantity: number;
  rate: number;
  discountPercent?: number;
  taxRate?: number;
}

export interface InvoiceRequestHistory {
  at: string;
  byName?: string;
  from?: string;
  to?: string;
  note?: string;
}

export interface ApprovalRecord {
  stage: 'quotation' | 'proforma' | 'invoice';
  byName: string;
  /** `owner` appears only in records written before Owner and Superadmin were unified. */
  role: 'owner' | 'accounts' | 'superadmin';
  status: 'approved' | 'rejected' | 'revision_requested';
  note?: string;
  at: string;
}

/**
 * What the server computes for each row so the client does not have to.
 *
 * These are rendering hints only. Every one of them is re-derived and re-checked
 * inside the route before anything is written, so a stale page can offer an
 * action but cannot complete one.
 */
export interface ApprovalSummary {
  /** The stage currently awaiting a decision, or null between/after stages. */
  stage: ApprovalStage | null;
  /** The single signature the current user may give, if any. */
  approvalRole: ApproverRole | null;
  requiredApprovers: ApproverRole[];

  quotationApprovals: StageApprovals;
  proformaApprovals: StageApprovals;
  quotationWaitingOn: WaitingOn;
  proformaWaitingOn: WaitingOn;
  quotationFullyApproved: boolean;
  proformaFullyApproved: boolean;

  /** Who the stage in play is waiting on. */
  waitingOn: WaitingOn;

  canApproveNow: boolean;
  canRejectNow: boolean;
  canSubmit: boolean;
  canRequestProforma: boolean;
  canResubmit: boolean;
  canResubmitProforma: boolean;
  canResubmitInvoice: boolean;
  canGenerateInvoice: boolean;
  canDelete: boolean;
  isLocked?: boolean;
}

export interface InvoiceRequest extends Partial<ApprovalSummary> {
  id: string;
  number: string;
  requestType?: 'quotation' | 'proforma' | 'invoice';
  clientName?: string;
  clientEmail?: string;
  clientPhone?: string;
  clientAddress?: string;
  clientGstin?: string;
  clientState?: string;
  sourceQuotationId?: string;
  sourceQuotationNumber?: string;
  sourceProformaId?: string;
  parentProformaId?: string;
  proformaId?: string;
  proformaNumber?: string;
  invoiceId?: string;
  invoiceNumber?: string;
  status: InvoiceRequestStatus;
  date?: string;
  items: InvoiceRequestLine[];
  currency: string;
  subtotal: number;
  taxTotal: number;
  total: number;
  paymentSchedule?: any;
  milestones?: any[];
  template_data?: any;
  notes?: string;
  reviewNote?: string;
  requestedByName?: string;
  /** The salesperson named on the quotation; copied down the proforma/tax invoice chain. */
  preparedBy?: string;
  requestedAt: string;
  reviewedByName?: string;
  reviewedAt?: string | null;
  history?: InvoiceRequestHistory[];
  approvals?: ApprovalRecord[];
  ownerName?: string;
  ownerId?: string;
  canReview?: boolean;
  canApproveAsOwner?: boolean;
  currentHtmlBody?: string;
  proformaHtmlBody?: string;
  currentRevisionNumber?: number;
  currentRevisionLabel?: string;
  resubmissionCount?: number;
  documentRevisions?: Array<{
    revisionNumber: number;
    revisionLabel?: string;
    createdByName?: string;
    createdAt?: string;
    note?: string;
    htmlBody: string;
    amount?: number;
    status?: string;
    rejectionNote?: string;
  }>;
  _id?: string;
  expenses?: any[];
  hasReceipt?: boolean;
  receiptCount?: number;
  employeeName?: string;
  amountMinor?: number;
  milestoneProformaNumber?: string;
  milestoneInvoiceNumber?: string;
  milestoneIndex?: number;
  milestonePhaseLabel?: string;
  invoiceApprovedBy?: string;
  /** Set when the document was emailed to the client from the app. */
  emailSentAt?: string | null;
  supersededById?: string | null;
  supersededByNumber?: string | null;
  revisedFromNumber?: string | null;
  /** Flat quotation-stage flags kept for the existing list UI. */
  ownerApproved?: boolean;
  accountsApproved?: boolean;
  isConfirmed?: boolean;
  isLocked?: boolean;
}

export interface InvoiceRequestListResponse {
  requests: InvoiceRequest[];
  statusCounts: Partial<Record<InvoiceRequestStatus, number>>;
  /** Server-authoritative: only Accounts/admin/superadmin may see the whole queue. */
  canReview: boolean;
  canApproveAsOwner?: boolean;
}

const KEY = ['invoice-requests'];

/** Human labels for each status, used for badges and the Waiting On column. */
export const STATUS_LABELS: Record<InvoiceRequestStatus, string> = {
  draft: 'Draft',
  requested: 'Awaiting Superadmin',
  under_review: 'Awaiting Superadmin',
  approved: 'Created',
  proforma_requested: 'Proforma Awaiting Approval',
  proforma_under_review: 'Proforma Awaiting Approval',
  proforma_approved: 'Proforma Approved',
  proforma_rejected: 'Proforma Rejected',
  invoice_pending_approval: 'Awaiting Superadmin Tax Invoice Approval',
  revision_needed: 'Revision Needed',
  rejected: 'Rejected',
  sent: 'Invoiced',
  confirmed: 'Proforma Approved',
};

export const APPROVER_LABELS: Record<WaitingOn, string> = {
  accounts: 'Accounts',
  superadmin: 'Superadmin',
  none: '—',
};

export function useInvoiceRequests(status?: InvoiceRequestStatus | '') {
  return useQuery({
    queryKey: [...KEY, 'list', status || 'all'],
    queryFn: async () => {
      const qs = status ? `?status=${encodeURIComponent(status)}` : '';
      const { data } = await api.get<InvoiceRequestListResponse>(`/invoice-requests${qs}`);
      return data;
    },
    // 15s polling so a signature given elsewhere shows up without a page reload.
    // There is no socket client in this app, so the live view is a poll.
    refetchInterval: 15000,
  });
}

/**
 * Every workflow action, one mutation each, declared unconditionally so hook
 * order stays stable across renders.
 *
 * The server re-checks authority and sequence on every call. The `can*` flags
 * from the list response only decide what the UI offers, never what is allowed —
 * an out-of-turn approval comes back 409 and an unauthorized one 403.
 */
export function useInvoiceRequestActions() {
  const qc = useQueryClient();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: KEY });
    // Quotations, payment schedules, milestones, and accounts dashboards read the same documents.
    qc.invalidateQueries({ queryKey: ['quotations'] });
    qc.invalidateQueries({ queryKey: ['payment-schedules'] });
    qc.invalidateQueries({ queryKey: ['milestones'] });
    qc.invalidateQueries({ queryKey: ['accounts-invoices'] });
    qc.invalidateQueries({ queryKey: ['accounting-schedules'] });
  };

  const post = async (id: string, action: string, body?: Record<string, unknown>) => {
    const { data } = await api.post(`/invoice-requests/${id}/${action}`, body ?? {}, { skipErrorToast: true });
    return data;
  };

  // ── Sales-side ────────────────────────────────────────────────────────────
  const submit = useMutation({
    mutationFn: ({ id }: { id: string }) => post(id, 'submit'),
    onSuccess: invalidate,
  });
  const requestProforma = useMutation({
    mutationFn: ({ id, note, milestoneIndex, milestonePhaseLabel }: { id: string; note?: string; milestoneIndex?: number; milestonePhaseLabel?: string }) =>
      post(id, 'request-proforma', { note, milestoneIndex, milestonePhaseLabel }),
    onSuccess: invalidate,
  });
  const resubmitProforma = useMutation({
    mutationFn: ({ id, note }: { id: string; note?: string }) => post(id, 'resubmit-proforma', { note }),
    onSuccess: invalidate,
  });
  const resubmitInvoice = useMutation({
    mutationFn: ({ id, note }: { id: string; note?: string }) => post(id, 'resubmit-invoice', { note }),
    onSuccess: invalidate,
  });
  const deleteDraft = useMutation({
    mutationFn: ({ id }: { id: string }) =>
      api.delete(`/invoice-requests/${id}`, { skipErrorToast: true }).then(r => r.data),
    onSuccess: invalidate,
  });

  // ── Approvals ─────────────────────────────────────────────────────────────
  //
  // Quotations need no approval; a proforma and a tax invoice are approved by the superadmin. The
  // server re-checks who may approve and refuses anything out of turn.
  const approve = useMutation({
    mutationFn: ({ id, stage, note }: { id: string; stage: ApprovalStage; note?: string }) => {
      if (stage === 'quotation') {
        throw new Error('Quotations no longer need approval');
      }
      return post(id, stage === 'proforma' ? 'approve-proforma' : 'approve-invoice-generation', { note });
    },
    onSuccess: invalidate,
  });
  const reject = useMutation({
    mutationFn: ({ id, stage, reason }: { id: string; stage: ApprovalStage; reason: string }) =>
      post(id, stage === 'proforma' ? 'reject-proforma' : 'reject', { reason, note: reason }),
    onSuccess: invalidate,
  });

  // ── Accounts-side review helpers (no signature attached) ──────────────────
  const review = useMutation({
    mutationFn: ({ id }: { id: string }) => post(id, 'review'),
    onSuccess: invalidate,
  });
  const requestRevision = useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) => post(id, 'request-revision', { note }),
    onSuccess: invalidate,
  });

  /** The final step: Accounts only, and only from a fully approved Proforma. */
  const generateInvoice = useMutation({
    mutationFn: ({ id }: { id: string }) => post(id, 'generate-invoice'),
    onSuccess: invalidate,
  });

  const requestInvoice = useMutation({
    mutationFn: ({ id, note }: { id: string; note?: string }) => post(id, 'request-invoice-approval', { note }),
    onSuccess: invalidate,
  });

  const approveInvoiceGeneration = useMutation({
    mutationFn: ({ id }: { id: string }) => post(id, 'approve-invoice-generation'),
    onSuccess: invalidate,
  });

  /** Standalone proforma creation — not linked to an existing quotation request. */
  const createProforma = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      api.post('/invoice-requests', { ...payload, requestType: 'proforma' }, { skipErrorToast: true }).then(r => r.data),
    onSuccess: invalidate,
  });

  return {
    submit, requestProforma, resubmitProforma, resubmitInvoice, deleteDraft,
    approve, reject,
    review, requestRevision,
    generateInvoice, requestInvoice, approveInvoiceGeneration,
    createProforma,
  };
}
